<script setup lang="ts">
import { computed } from "vue";

import {
  upperLevelLinksFeatureSelected,
  upperLevelLinksLinkedSelected,
  upperLevelLinksUnlinkedSelected,
} from "@/analytics/events/search";
import { trackEvent } from "@/analytics/track";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListFooter from "@/components/ui/MenuList/MenuListFooter.vue";
import MenuListHeader from "@/components/ui/MenuList/MenuListHeader.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListSubheader from "@/components/ui/MenuList/MenuListSubheader.vue";
import MenuListTitle from "@/components/ui/MenuList/MenuListTitle.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import TypeDot from "@/components/ui/TypeDot/TypeDot.vue";
import { isBacklogBoard } from "@/model/board";
import type { LinkableCard } from "@/model/link";
import { useBoardStore } from "@/store/board";
import { useLinkStore } from "@/store/link";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { useTeamStore } from "@/store/team";

import UpperLevelLinksTooltip from "../tooltips/UpperLevelLinksTooltip.vue";
import SearchDropdown from "./SearchDropdown.vue";

const teamStore = useTeamStore();
const searchMenuStore = useSearchMenuStore();

const groupedByTeamName = computed(() => {
  const linkedBacklogFeaturesMap = new Map<string, LinkableCard>();
  const noTeamFeaturesMap = new Map<string, LinkableCard>();

  const currentTeam = useTeamStore().current;

  const cards = useBoardStore().currentBoard().cards;
  for (const card of Object.values(cards)) {
    const linkedItems = useLinkStore()
      .getLinkedItems({ type: "sticky", id: card.data.id })
      .filter(
        (link) => link.kind === "sticky" && isBacklogBoard(link.type.origin),
      ) as LinkableCard[];

    for (const link of linkedItems) {
      if (link.team?.id) {
        linkedBacklogFeaturesMap.set(link.id, link);
      } else {
        noTeamFeaturesMap.set(link.id, link);
      }
    }
  }

  // Group features by team name
  const grouped: Record<string, LinkableCard[]> = {};

  for (const card of linkedBacklogFeaturesMap.values()) {
    const teamName = teamStore.findTeam({ id: card.team?.id })?.name;
    if (teamName) {
      if (!grouped[teamName]) {
        grouped[teamName] = [];
      }
      grouped[teamName].push(card);
    }
  }

  // Sort team entries so current team comes last
  const sortedEntries = Object.entries(grouped).sort(([teamNameA], [teamNameB]) => {
    const aIsCurrentTeam = teamNameA === currentTeam?.name;
    const bIsCurrentTeam = teamNameB === currentTeam?.name;

    if (aIsCurrentTeam && !bIsCurrentTeam) return 1; // current team comes last
    if (!aIsCurrentTeam && bIsCurrentTeam) return -1; // current team comes last
    return teamNameA.localeCompare(teamNameB); // alphabetical for others
  });

  return { sortedEntries, noTeamFeatures: Array.from(noTeamFeaturesMap.values()) };
});

const hasSelection = computed(() => searchMenuStore.selectedUpperLevelLinks.type !== "none");

const handleSelectLinked = () => {
  searchMenuStore.selectedUpperLevelLinks = { type: "linked" };
  trackEvent(upperLevelLinksLinkedSelected());
};

const handleSelectUnlinked = () => {
  searchMenuStore.selectedUpperLevelLinks = { type: "unlinked" };
  trackEvent(upperLevelLinksUnlinkedSelected());
};

const handleSelectFeature = (feature: LinkableCard) => {
  const current = searchMenuStore.selectedUpperLevelLinks;
  if (current.type === "sticky" && current.stickyId === feature.id) {
    return;
  }
  searchMenuStore.selectedUpperLevelLinks = { type: "sticky", stickyId: feature.id };
  trackEvent(upperLevelLinksFeatureSelected(feature.id, "search"));
};

const handleClear = () => {
  searchMenuStore.selectedUpperLevelLinks = { type: "none" };
};
</script>

<template>
  <SearchDropdown
    width="xl"
    name="upper-level-links"
    icon="general/external-links"
    label-key="label.upperLevelLinks"
    :selected="hasSelection"
  >
    <template #tooltip>
      <UpperLevelLinksTooltip :selection="searchMenuStore.selectedUpperLevelLinks" />
    </template>

    <MenuList selectable>
      <MenuListHeader>
        <MenuListTitle>{{ $t("label.upperLevelLinks") }}</MenuListTitle>
      </MenuListHeader>
      <MenuListItems v-focus-trap>
        <MenuListItem
          :selected="searchMenuStore.selectedUpperLevelLinks.type === 'linked'"
          @click="handleSelectLinked"
        >
          <template #before><SvgIcon name="general/link" /></template>
          {{ $t("label.linkedOnly") }}
        </MenuListItem>

        <MenuListItem
          :selected="searchMenuStore.selectedUpperLevelLinks.type === 'unlinked'"
          @click="handleSelectUnlinked"
        >
          <template #before><SvgIcon name="general/link-remove" /></template>
          {{ $t("label.unlinkedOnly") }}
        </MenuListItem>

        <template v-if="groupedByTeamName.noTeamFeatures.length > 0">
          <MenuListSubheader>{{ $t("general.noTeam") }}</MenuListSubheader>

          <MenuListItem
            v-for="feature in groupedByTeamName.noTeamFeatures"
            :key="feature.id"
            :selected="
              searchMenuStore.selectedUpperLevelLinks.type === 'sticky' &&
              searchMenuStore.selectedUpperLevelLinks.stickyId === feature.id
            "
            @click="handleSelectFeature(feature)"
          >
            <template #before>
              <TypeDot class="feature-dot" :color="feature.type.color" size="medium" />
            </template>
            {{ feature.text }}
          </MenuListItem>
        </template>

        <template v-for="[teamName, features] in groupedByTeamName.sortedEntries" :key="teamName">
          <MenuListSubheader>
            {{ teamName }}
          </MenuListSubheader>

          <MenuListItem
            v-for="feature in features"
            :key="feature.id"
            :selected="
              searchMenuStore.selectedUpperLevelLinks.type === 'sticky' &&
              searchMenuStore.selectedUpperLevelLinks.stickyId === feature.id
            "
            @click="handleSelectFeature(feature)"
          >
            <template #before>
              <TypeDot class="feature-dot" :color="feature.type.color" size="medium" />
            </template>
            {{ feature.text }}
          </MenuListItem>
        </template>
      </MenuListItems>
      <MenuListFooter :disabled="!hasSelection" @click="handleClear" />
    </MenuList>
  </SearchDropdown>
</template>
